import React, { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ResizeObserver from "resize-observer-polyfill";
import * as d3 from "d3";

import { getChart } from "../../../store/slices/chart";
import formatNumber from "../../../hooks/formatNumber";
import parseTimeString from "../../../hooks/parseTimeString";
import {getChartForecast} from "../../../store/slices/chartforecast";
import {
	IChartData,
	IChartForecastData,
	IDashboardConfig,
	IObjectKeys
} from "../../../constants/interfaces";
import {ensureType} from "../../../hooks/ensureType";
import moment from "moment-timezone";
import formatPostedOnChannels from "../../../hooks/formatPostedOnChannels";
import { useParams } from "react-router-dom";

/* Percentage of whitespace to show above graph */
const RANGE_EXTEND = 5;
// Gojko
/* Mapping of field names to line properties */
const LINE_TYPES: IObjectKeys = {
	totalWebViews: {
		label: "web",
		className: "web",
	},
	totalAppViews: {
		label: "app",
		className: "app",
	},
	totalViews: {
		label: "totals",
		className: "totals",
	},
	totalVideoViews: {
		label: "totals",
		className: "totals",
	},
	totalVideoWebViews: {
		label: "web",
		className: "web",
	},
	totalVideoAppViews: {
		label: "app",
		className: "app",
	},
	conversion: {
		label: "conversion",
		className: "totals",
	},
};

const getFields = (metric: string, data: IChartData) => {
	if (data) {
		let fields: Array<string>;
		switch (metric) {
			case "views":
			case "storyviews":
				fields = ["totalWebViews", "totalAppViews", "totalViews"];
				break;
			case "conversions":
				fields = ["conversion"];
				break;
			case "events":
				fields = ["totalEvents"];
				break;
			default:
				fields = ["totalVideoViews", "totalVideoWebViews", "totalVideoAppViews"];
		}
		/* eslint-disable */
		return  fields.filter(field => {
			// get sum of each metric values
			const sum = data.map(item => item[field]).reduce((a, b) => a + b, 0);
			// Don't return field if the sum of the values is zero
			if (sum !== 0) {
				return field;
			}
		});
		/* eslint-disable */
	}
	return [];
};

export const useResizeObserver = (ref: any) => {
	const [dimensions, setDimensions] = useState<DOMRectReadOnly | null>(null);
	useEffect(() => {
		const observeTarget = ref.current;
		const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
			entries.forEach((entry: ResizeObserverEntry) => {
				setDimensions(entry.contentRect);
			});
		});
		resizeObserver.observe(observeTarget);
		return () => {
			resizeObserver.unobserve(observeTarget);
		};
	}, [ref]);
	return dimensions;
};

/**
 * Find min and max value in data points over all given fields..
 */
const findBounds = (dataPoints: IChartData, fields: Array<string>) => {
	const fieldValues = fields.map((f) => dataPoints.map((t) => t[f]));
	const values = fieldValues.reduce((p, n) => p.concat(n));
	return  [Math.min(...values), Math.max(...values)];
};

type Props = {
	cellId: number
}

const ChartWidget = (props: Props) => {
	const widgetConfig = useSelector(
		(state: {dashboard: {data: IDashboardConfig}}) => ensureType(state.dashboard.data.grid_config.find(cell => cell.cell_id === props.cellId))
	);
	const brandsettings = useSelector(
		(state: {brandsettings: {data: any}}) => ensureType(state.brandsettings.data)
	);
	const timezone = brandsettings && brandsettings.domainSettings && brandsettings.domainSettings.timezone ? brandsettings.domainSettings.timezone : moment.tz.guess();
	const metric = widgetConfig.graph_metric;
	const data = useSelector((state: {chart: {data: {[key: string]: IChartData} }}) => state.chart.data[props.cellId]);
	const forecast = useSelector((state: {chartforecast: { data: {[key: string]: IChartForecastData} }}) => state.chartforecast.data[props.cellId]);
	const dashboardId = useSelector((state: any) => state.dashboard.currentDashboardId)
	const dispatch = useDispatch();
	const {id} = useParams() as any;
	const [graphData, setGraphData] = useState(data);

	const fromTimestamp = new Date(moment().tz(timezone).startOf("day").format('YYYY-MM-DDTHH:mm:ss'));
	const toTimestamp =  new Date(moment().tz(timezone).endOf("day").format('YYYY-MM-DDTHH:mm:ss'));

	const refreshRate = parseTimeString(widgetConfig.refresh);
	const fields = getFields(metric, graphData);
	const title = widgetConfig.label;
	const brands = widgetConfig.brands.join(',');
	const extraInfo = "Today";
	const subtitle = widgetConfig.subtitle ? widgetConfig.subtitle : null;
	const d3Container: any = useRef();
	const apiInterval = "midnight";
	const apiPubDateInterval = widgetConfig.pubdate_interval;
	const sections = widgetConfig.sections.join('$$');
	const referrers = widgetConfig.referrers ? widgetConfig.referrers.join('$$') : '';
	const referrerTypes = widgetConfig.referrer_types ? widgetConfig.referrer_types.join('$$') : '';
	const tags = widgetConfig.tags ? widgetConfig.tags.join('$$') : '';
	const userNeeds = widgetConfig.user_needs ? widgetConfig.user_needs.join('$$') : '';
	const conversions = widgetConfig.conversions ? widgetConfig.conversions.join('$$') : '';
	let eventFields = '';

	if (widgetConfig.event_fields && widgetConfig.event_fields.length > 0) {
		eventFields = widgetConfig.event_fields[0].name;
		LINE_TYPES.totalEvents = {
			label: widgetConfig.event_fields[0].short_label,
			className: "totals"
		};
	}

	const isPaid = widgetConfig.paid_type;
	const videoFilter = widgetConfig.video_filter;
	const pageType = widgetConfig.page_type;
	const { includeChannels, excludeChannels } = formatPostedOnChannels(widgetConfig.posted_on_channels);

	const wrapperRef: any = useRef();
	const headerRef: any = useRef();
	const dimensions = useResizeObserver(wrapperRef);
	const headerDimensions = useResizeObserver(headerRef);
	const yOffset = 40;

	// initial first load
	useEffect(() => {
		dispatch(getChart(metric, brands, apiInterval, sections, tags, conversions, eventFields, isPaid, videoFilter, pageType, referrers, referrerTypes, includeChannels, excludeChannels, apiPubDateInterval, userNeeds, props.cellId));
	}, [dispatch, metric, brands, apiInterval, sections, tags, conversions, eventFields, isPaid, videoFilter, pageType, referrers, referrerTypes, includeChannels, excludeChannels, apiPubDateInterval, userNeeds]);

	// update data
	useEffect(() => {
		const interval = setInterval(() => {
			dispatch(getChart(metric, brands, apiInterval, sections, tags, conversions, eventFields, isPaid, videoFilter, pageType, referrers, referrerTypes, includeChannels, excludeChannels, apiPubDateInterval, userNeeds, props.cellId));
		}, refreshRate);
		return () => clearInterval(interval);
	}, [dispatch, refreshRate, metric, brands, apiInterval, sections, tags, conversions, eventFields, isPaid, videoFilter, pageType, referrers, referrerTypes, includeChannels, excludeChannels, apiPubDateInterval, userNeeds, props.cellId]);

	// get relevant data from state array
	useEffect(() => {
		setGraphData(data);
	}, [data, widgetConfig]);

	// get forecast
	useEffect(() => {
		const brands = widgetConfig.brands;
		const sections = widgetConfig.sections;
		const paid_type = widgetConfig.paid_type || '';
		const video_filter = widgetConfig.video_filter || '';
		const referrers = widgetConfig.referrers || '';
		const referrerTypes = widgetConfig.referrer_types || '';
		const tags = widgetConfig.tags || [];
		const conversions = widgetConfig.conversions || [];
		const posted_on_channels = widgetConfig.posted_on_channels || [];
		const page_type = widgetConfig.page_type || '';

		const hasMetadataFilters = brands.length > 1 || sections.length > 0 || tags.length > 0 || conversions.length > 0 || paid_type !== '' || video_filter !== '' || referrers.length > 0 || referrerTypes.length > 0 || posted_on_channels.length > 0 || page_type === 'article' || page_type === 'landing' || apiPubDateInterval || userNeeds !== '';

		// do not show forecast if metric is not views or there are filters applied
		if (metric === "views" && !hasMetadataFilters && id === dashboardId) {
			const dayOfTheWeek = new Date().getDay();
			const brand = brands[0] ? brands[0] : null;
			dispatch(getChartForecast(metric, brand, dayOfTheWeek, props.cellId));
		}
	}, [dispatch, metric, widgetConfig.brands]);

	useEffect(() => {
		if (!dimensions || !headerDimensions || !graphData || (graphData.length === 0)) return;
		const currentData = graphData;
		const startDate = fromTimestamp;
		const endDate = toTimestamp;

		const [rangeStart, valueRangeEnd] = findBounds(currentData, fields);
		const yAxisTicks = valueRangeEnd < 10 ? valueRangeEnd : 10;
		const rangeEnd = valueRangeEnd * (1 + RANGE_EXTEND / 100);
		// mold forecast data to suitable values
		const forecastArray = (forecast && widgetConfig.brands.length <= 1) ? forecast : [];

		const pairs = [];
		//forecast values are like 2 lines first half going in future and from half returning to past in our case we need min and max at same time.
		for (let i = 0; i < forecastArray.length / 2; i++) {
			pairs.push({
				maxValue:forecastArray[i],
				minValue:forecastArray[forecastArray.length - 1 - i],
			})
		}

		const addMinutesToDate = function (minutes:number) {
			return  moment().tz(timezone).startOf("day").add(minutes, 'minutes').valueOf();
		};
		const forecastValues = pairs.map((item, i) => ({ timestamp: addMinutesToDate( 5 * i), y0: item.minValue, y1: item.maxValue }));
		const svg = d3.select(d3Container.current);
		const xScale = d3
			.scaleTime()
			.range([30, dimensions.width-10])
			.domain([startDate, endDate]);

		const yScale = d3
			.scaleLinear()
			.range([dimensions.height - headerDimensions.height - yOffset, 0])
			.domain([rangeStart, rangeEnd]);

		const xAxis= d3
			.axisBottom<Date>(xScale)
			.tickFormat((d) => `${d.getHours()}h`);
		const yAxis = d3.axisRight(yScale).ticks(yAxisTicks).tickFormat((num) => formatNumber(Number(num)));

		svg.select(".x-axis")
			.style(
				"transform",
				`translateY(${
					dimensions.height - headerDimensions.height - yOffset
				}px)`
			)
			// @ts-ignore
			.call(xAxis);
		// @ts-ignore
		svg.select(".y-axis").style("transform", "translateX(0px)").call(yAxis);

		svg.selectAll(".grid").remove();

		svg.append("g")
			.attr("class", "grid")
			.call(
				d3
					// @ts-ignore
					.axisLeft()
					// @ts-ignore
					.scale(yScale)
					// @ts-ignore
					.tickSize(-dimensions.width, 0, 0)
					.tickFormat(null)
			);

		svg.selectAll(".now").remove();

		svg.append("line")
			.attr("class", "now")
			.attr("x1", xScale(new Date(moment().tz(timezone).format('YYYY-MM-DDTHH:mm:ss'))))
			.attr("x2", xScale(new Date(moment().tz(timezone).format('YYYY-MM-DDTHH:mm:ss'))))
			.attr("y1", yScale(rangeStart))
			.attr("y2", yScale(rangeEnd));

		const fieldLine = (field: string) =>
			d3
				.line()
				.x((value) => {
					// @ts-ignore
					return xScale( new Date(moment(value.date).tz(timezone).format('YYYY-MM-DDTHH:mm:ss')))
				})
				.y((value) => {
					// @ts-ignore
					return yScale(value[field])
				})
				.curve(d3.curveCardinal);

		const areaGenerator =
			d3.area()
				.x((value) => {
					// @ts-ignore
					return xScale( new Date(moment(value.timestamp).tz(timezone).format('YYYY-MM-DDTHH:mm:ss')))
				})
				// @ts-ignore
				.y0((value) => yScale(value.y0))
				// @ts-ignore
				.y1((value) => yScale(value.y1))
				.curve(d3.curveCardinal);

		svg.selectAll(".lineRange")
			.data([forecastValues])
			.join("path")
			// @ts-ignore
			.attr("d", (value) => areaGenerator(value))
			.attr("class", "lineRange")
			.attr("fill", "none")
			.attr("stroke", "none");

		fields.forEach((field) => {
			const line: any = fieldLine(field);
			const className = `line-${LINE_TYPES[field].className}`;
			svg.selectAll(`.${className}`)
				.data([currentData])
				.join("path")
				.attr("d", line)
				.attr("class", className)
				.attr("fill", "none")
				.attr("stroke", "white");
		});

	}, [
		graphData,
		fields,
		dimensions,
		headerDimensions,
		fromTimestamp,
		toTimestamp,
		forecast,
		widgetConfig,
		metric
	]);

	let graphHTML = (<div>Loading...</div>);
	if (graphData) {
		graphHTML = (
			<svg
				className="d3-component"
				width={"100%"}
				height={"100%"}
				ref={d3Container}
			>
				<g className="x-axis" />
				<g className="y-axis" />
			</svg>
		)
	}

	return (
		<div className="linechart" ref={wrapperRef}>
			<div className="linechart__heading" ref={headerRef}>
				<div className="linechart__heading-first-row">
					<span className="linechart__title">{title}</span>
					{subtitle && (
						<span className="linechart__subtitle">{subtitle}</span>
					)}
					<div className="linechart__heading-right">
						<div style={{flex:1}}/>
						<ul className="legend">
							{fields
								.map((field) => [field, LINE_TYPES[field]])
								.map(([field, props]) => {
									return props ? (
										<li
										key={field}
										className={[
											`legend-${props.className}`,
											"legend__item",
										].join(" ")}
										>
											{props.label}
										</li>
									) : null
							})}
						</ul>
					</div>
				</div>
				<span className="linechart__extra-info">{extraInfo}</span>
			</div>
			{graphHTML}
		</div>
	);
};

export default ChartWidget;
